import { AccrualDocumentImporterMode } from "./accrualDocumentImporter.lib";
import { AccrualImporterDocumentRow } from "./accrualImporter.types";
import {
    getAccrualDocumentImportStatus,
    getAccrualLineItemExtraction,
} from "./accrualImporter.helpers";

export enum AccrualImporterDraftSort {
    CREATED_AT_ASC = "CREATED_AT_ASC",
    CREATED_AT_DESC = "CREATED_AT_DESC",
    DOCUMENT_DATE_ASC = "DOCUMENT_DATE_ASC",
    DOCUMENT_DATE_DESC = "DOCUMENT_DATE_DESC",
    DUE_DATE_ASC = "DUE_DATE_ASC",
    DUE_DATE_DESC = "DUE_DATE_DESC",
    DOCUMENT_NUMBER_ASC = "DOCUMENT_NUMBER_ASC",
    DOCUMENT_NUMBER_DESC = "DOCUMENT_NUMBER_DESC",
    COUNTERPARTY_ASC = "COUNTERPARTY_ASC",
    COUNTERPARTY_DESC = "COUNTERPARTY_DESC",
    IMPORT_STATUS_ASC = "IMPORT_STATUS_ASC",
    IMPORT_STATUS_DESC = "IMPORT_STATUS_DESC",
    TOTAL_ASC = "TOTAL_ASC",
    TOTAL_DESC = "TOTAL_DESC",
    ENTITY_ASC = "ENTITY_ASC",
    ENTITY_DESC = "ENTITY_DESC",
}

function compareStrings(
    left: string | null | undefined,
    right: string | null | undefined,
    asc: boolean,
): number {
    const leftEmpty = !left;
    const rightEmpty = !right;

    if (leftEmpty && rightEmpty) {
        return 0;
    }
    if (leftEmpty) {
        return 1;
    }
    if (rightEmpty) {
        return -1;
    }

    const cmp = left.localeCompare(right);
    return asc ? cmp : -cmp;
}

function compareNumbers(
    left: number | null | undefined,
    right: number | null | undefined,
    asc: boolean,
): number {
    const leftEmpty = left == null || Number.isNaN(left);
    const rightEmpty = right == null || Number.isNaN(right);

    if (leftEmpty && rightEmpty) {
        return 0;
    }
    if (leftEmpty) {
        return 1;
    }
    if (rightEmpty) {
        return -1;
    }

    return asc ? left - right : right - left;
}

function getCreatedAtTime(row: AccrualImporterDocumentRow): number | null {
    const createdAt = row.financialDocument?.createdAt;
    if (!createdAt) {
        return null;
    }
    const time = new Date(createdAt).getTime();
    return Number.isNaN(time) ? null : time;
}

export function compareUploadsThenNewest(
    leftRow: AccrualImporterDocumentRow,
    rightRow: AccrualImporterDocumentRow,
): number {
    const uploadDifference =
        Number(Boolean(leftRow.financialDocument)) -
        Number(Boolean(rightRow.financialDocument));
    if (uploadDifference !== 0) {
        return uploadDifference;
    }

    return compareNumbers(
        getCreatedAtTime(leftRow),
        getCreatedAtTime(rightRow),
        false,
    );
}

function getImportStatusRank(row: AccrualImporterDocumentRow): number {
    const status = getAccrualDocumentImportStatus(row);
    switch (status.kind) {
        case "uploading":
            return 0;
        case "processing":
            return 1;
        case "extraction_failed":
            return 2;
        case "review_tasks":
            return 3;
        case "import_failed_retryable":
            return 4;
        case "ready":
        case "ready_with_warnings":
            return 5;
        case "import_scheduled":
            return 6;
        case "importing":
            return 7;
        case "imported":
            return 8;
    }
}

function getExtraction(
    row: AccrualImporterDocumentRow,
    mode: AccrualDocumentImporterMode,
) {
    const financialDocument = row.financialDocument;
    if (!financialDocument) {
        return null;
    }
    return getAccrualLineItemExtraction(financialDocument, mode);
}

function getEntityName(
    row: AccrualImporterDocumentRow,
    entityNames: ReadonlyMap<number, string>,
): string | null {
    const entityId = row.financialDocument?.entityId;
    if (entityId == null) {
        return null;
    }
    return entityNames.get(entityId) ?? null;
}

export function sortAccrualImporterDraftRows({
    rows,
    sort,
    mode,
    entityNames,
}: {
    rows: AccrualImporterDocumentRow[];
    sort: AccrualImporterDraftSort | undefined;
    mode: AccrualDocumentImporterMode;
    entityNames: ReadonlyMap<number, string>;
}): AccrualImporterDocumentRow[] {
    const sortedRows = [...rows];
    if (!sort) {
        sortedRows.sort(compareUploadsThenNewest);
        return sortedRows;
    }

    sortedRows.sort((leftRow, rightRow) => {
        const leftExtraction = getExtraction(leftRow, mode);
        const rightExtraction = getExtraction(rightRow, mode);

        switch (sort) {
            case AccrualImporterDraftSort.CREATED_AT_ASC:
                return compareNumbers(
                    getCreatedAtTime(leftRow),
                    getCreatedAtTime(rightRow),
                    true,
                );
            case AccrualImporterDraftSort.CREATED_AT_DESC:
                return compareNumbers(
                    getCreatedAtTime(leftRow),
                    getCreatedAtTime(rightRow),
                    false,
                );
            case AccrualImporterDraftSort.DOCUMENT_DATE_ASC:
                return compareStrings(
                    leftExtraction?.documentDate,
                    rightExtraction?.documentDate,
                    true,
                );
            case AccrualImporterDraftSort.DOCUMENT_DATE_DESC:
                return compareStrings(
                    leftExtraction?.documentDate,
                    rightExtraction?.documentDate,
                    false,
                );
            case AccrualImporterDraftSort.DUE_DATE_ASC:
                return compareStrings(
                    leftExtraction?.dueDate,
                    rightExtraction?.dueDate,
                    true,
                );
            case AccrualImporterDraftSort.DUE_DATE_DESC:
                return compareStrings(
                    leftExtraction?.dueDate,
                    rightExtraction?.dueDate,
                    false,
                );
            case AccrualImporterDraftSort.DOCUMENT_NUMBER_ASC:
                return compareStrings(
                    leftExtraction?.documentNumber,
                    rightExtraction?.documentNumber,
                    true,
                );
            case AccrualImporterDraftSort.DOCUMENT_NUMBER_DESC:
                return compareStrings(
                    leftExtraction?.documentNumber,
                    rightExtraction?.documentNumber,
                    false,
                );
            case AccrualImporterDraftSort.COUNTERPARTY_ASC:
                return compareStrings(
                    leftExtraction?.counterpartyName,
                    rightExtraction?.counterpartyName,
                    true,
                );
            case AccrualImporterDraftSort.COUNTERPARTY_DESC:
                return compareStrings(
                    leftExtraction?.counterpartyName,
                    rightExtraction?.counterpartyName,
                    false,
                );
            case AccrualImporterDraftSort.IMPORT_STATUS_ASC:
                return (
                    getImportStatusRank(leftRow) - getImportStatusRank(rightRow)
                );
            case AccrualImporterDraftSort.IMPORT_STATUS_DESC:
                return (
                    getImportStatusRank(rightRow) - getImportStatusRank(leftRow)
                );
            case AccrualImporterDraftSort.TOTAL_ASC:
                return compareNumbers(
                    leftExtraction?.totalAmount,
                    rightExtraction?.totalAmount,
                    true,
                );
            case AccrualImporterDraftSort.TOTAL_DESC:
                return compareNumbers(
                    leftExtraction?.totalAmount,
                    rightExtraction?.totalAmount,
                    false,
                );
            case AccrualImporterDraftSort.ENTITY_ASC:
                return compareStrings(
                    getEntityName(leftRow, entityNames),
                    getEntityName(rightRow, entityNames),
                    true,
                );
            case AccrualImporterDraftSort.ENTITY_DESC:
                return compareStrings(
                    getEntityName(leftRow, entityNames),
                    getEntityName(rightRow, entityNames),
                    false,
                );
        }
    });

    return sortedRows;
}
